import React, { useState, useEffect, useRef } from 'react';
import { Student } from '../../../../utils/firebase';
import Card from '../../../../components/ui/Card';
import Button from '../../../../components/ui/Button';

interface ChopstickLotteryProps {
  students: Student[];
}

const ChopstickLottery: React.FC<ChopstickLotteryProps> = ({ students }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [winners, setWinners] = useState<Student[]>([]);
  const [showRanking, setShowRanking] = useState(false);
  const [currentRound, setCurrentRound] = useState(0);
  const animationRef = useRef<number | undefined>(undefined);
  const startTimeRef = useRef<number>(0);

  // 애니메이션 시작
  const startAnimation = () => {
    if (students.length === 0) return;
    
    setIsRunning(true);
    setShowRanking(false);
    setWinners([]);
    setCurrentRound(0);
    setSelectedIndex(-1);
    startTimeRef.current = Date.now();

    const shuffled = [...students].sort(() => Math.random() - 0.5);
    let round = 0;
    const selectedWinners: Student[] = [];

    const animate = () => {
      const elapsed = Date.now() - startTimeRef.current;
      const speed = 50 + (round * 10); // 라운드가 진행될수록 느려짐
      
      // 젓가락이 움직이는 애니메이션
      const index = Math.floor((elapsed / speed) % shuffled.length);
      setSelectedIndex(index);

      // 각 라운드마다 당첨자 선택
      const roundDuration = 2000 + (round * 500); // 라운드가 진행될수록 더 오래
      if (elapsed > roundDuration * (round + 1)) {
        if (round < shuffled.length) {
          const winner = shuffled[round];
          selectedWinners.push(winner);
          setCurrentRound(round + 1);
          round++;
        } else {
          // 모든 라운드 완료
          finishAnimation(selectedWinners);
        }
      }

      if (isRunning && round <= shuffled.length) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  };

  // 애니메이션 종료
  const finishAnimation = (selectedWinners: Student[]) => {
    setIsRunning(false);
    setWinners(selectedWinners);
    setShowRanking(true);
    setSelectedIndex(-1);
  };

  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  // 학생 목록 섞기 (애니메이션용)
  const shuffledStudents = React.useMemo(() => {
    return [...students].sort(() => Math.random() - 0.5);
  }, [students]);

  return (
    <Card style={{ padding: '2rem' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.5rem', color: '#161616' }}>
          젓가락 뽑기
        </h2>
        <p style={{ fontSize: '0.875rem', color: '#666', marginBottom: '1rem' }}>
          젓가락이 움직이며 학생을 선택합니다. 순서대로 당첨자가 결정됩니다.
        </p>
        <button
          type="button"
          onClick={startAnimation}
          disabled={isRunning || students.length === 0}
          style={{
            padding: '0.75rem 1.5rem',
            fontSize: '0.875rem',
            fontWeight: 500,
            backgroundColor: isRunning ? '#ccc' : '#4A90E2',
            color: '#fff',
            border: 'none',
            borderRadius: '0.375rem',
            cursor: isRunning ? 'not-allowed' : 'pointer'
          }}
        >
          {isRunning ? '뽑는 중...' : '추첨 시작'}
        </button>
      </div>

      {/* 젓가락 뽑기 영역 */}
      <div style={{ 
        position: 'relative',
        width: '100%',
        minHeight: '400px',
        backgroundColor: '#F8F8F8',
        borderRadius: '0.5rem',
        padding: '2rem',
        marginBottom: '1.5rem',
        border: '2px solid #E0E0E0',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        {/* 젓가락 컨테이너 */}
        <div style={{
          position: 'relative',
          width: '100%',
          maxWidth: '600px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          {shuffledStudents.map((student, index) => {
            const isSelected = selectedIndex === index;
            const isWinner = winners.some(w => w.uid === student.uid);
            
            return (
              <div
                key={student.uid}
                style={{
                  position: 'relative',
                  padding: '1rem',
                  backgroundColor: isSelected ? '#4A90E2' : isWinner ? '#FFA726' : '#fff',
                  borderRadius: '0.5rem',
                  border: isSelected ? '3px solid #1976D2' : isWinner ? '3px solid #FF8F00' : '2px solid #E0E0E0',
                  transition: 'all 0.2s',
                  transform: isSelected ? 'scale(1.1)' : 'scale(1)',
                  boxShadow: isSelected ? '0 4px 12px rgba(74, 144, 226, 0.4)' : '0 2px 4px rgba(0,0,0,0.1)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                {/* 젓가락 아이콘 */}
                <div style={{
                  fontSize: '2rem',
                  transform: isSelected ? 'rotate(15deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s'
                }}>
                  🥢
                </div>
                <span style={{ 
                  fontSize: '0.875rem', 
                  fontWeight: isSelected ? 600 : 400,
                  color: isSelected || isWinner ? '#fff' : '#161616',
                  textAlign: 'center'
                }}>
                  {student.name}
                </span>
                {isWinner && (
                  <span style={{
                    fontSize: '0.75rem',
                    color: '#fff',
                    fontWeight: 600
                  }}>
                    당첨!
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* 현재 선택 표시 */}
        {isRunning && selectedIndex >= 0 && (
          <div style={{
            padding: '1rem 2rem',
            backgroundColor: '#E3F2FD',
            borderRadius: '0.5rem',
            border: '2px solid #4A90E2'
          }}>
            <div style={{ fontSize: '1rem', fontWeight: 600, color: '#1976D2', textAlign: 'center' }}>
              🥢 {shuffledStudents[selectedIndex]?.name} 선택 중...
            </div>
          </div>
        )}

        {/* 진행 상황 */}
        {isRunning && (
          <div style={{ marginTop: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: '0.875rem', color: '#666' }}>
              {currentRound} / {students.length} 명 선택됨
            </div>
          </div>
        )}
      </div>

      {/* 순위 표시 */}
      {showRanking && winners.length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem', color: '#161616' }}>
            추첨 결과
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {winners.map((student, index) => (
              <div
                key={student.uid}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '0.75rem',
                  backgroundColor: index === 0 ? '#FFF4E6' : index === 1 ? '#F0F0F0' : index === 2 ? '#FFF8E1' : '#F8F8F8',
                  borderRadius: '0.375rem',
                  border: index === 0 ? '2px solid #FFA726' : '1px solid #E0E0E0'
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    backgroundColor: index === 0 ? '#FFA726' : index === 1 ? '#9E9E9E' : index === 2 ? '#FFC107' : '#E0E0E0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }}
                >
                  {index + 1}
                </div>
                <span style={{ fontSize: '0.875rem', fontWeight: 500, color: '#161616' }}>
                  {student.name}
                </span>
                {index === 0 && (
                  <span style={{ 
                    fontSize: '0.75rem', 
                    color: '#FFA726', 
                    fontWeight: 600,
                    marginLeft: 'auto'
                  }}>
                    🏆 당첨!
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

export default ChopstickLottery;






